import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_ENDPOINT } from "../../../config/config";

const initialState = {
    loading: false,
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    limit: 18,
    offset: 0,
    results: [],
    search: "",
    selectedPositions: ["All Positions"],
    position: [],
    draftPlayerId: "",
    playerIds: [],
};


const buildExcludeParam = (playerIds = [], chosenPlayerIds = []) =>
    [...new Set([...playerIds, ...chosenPlayerIds])]
        .map((id) => `exclude_ids=${id}`)
        .join("&");

const buildPositionParam = (position = []) =>
    position.length ? position.map((p) => `position=${p}`).join("&") : "";


export const getPlayersDraftRoom = createAsyncThunk(
    "playersDraftRoom/getPlayersDraftRoom",
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            const {
                playersDraftRoom: { limit, search, position, playerIds },
                stimulyatorDraftRoom: { chosenPlayerIds },
            } = getState();
            const offset = 0;
            const excludeIdsParam = buildExcludeParam(playerIds, chosenPlayerIds);
            const positionParam = buildPositionParam(position);
            const res = await axios.get(
                `${API_ENDPOINT}players/?${excludeIdsParam}&limit=${limit}&offset=${offset}&search=${search}&${positionParam}`
            );
            dispatch(
                setPlayersDraftRoom({
                    ...res.data,
                    limit,
                    search,
                    position,
                    currentPage: 1,
                    offset,
                })
            );
        } catch (error) {
            return rejectWithValue(error?.message || "Fetch error");
        }
    }
);


export const positionPlayersDraftRoom = createAsyncThunk(
    "playersDraftRoom/positionPlayersDraftRoom",
    async (setPosition, { dispatch, getState, rejectWithValue }) => {
        try {
            const {
                playersDraftRoom: { limit, search, playerIds },
                stimulyatorDraftRoom: { chosenPlayerIds },
            } = getState();
            const offset = 0;
            const excludeIdsParam = buildExcludeParam(playerIds, chosenPlayerIds);
            const positionParam = buildPositionParam(setPosition);
            const res = await axios.get(
                `${API_ENDPOINT}players/?${excludeIdsParam}&limit=${limit}&offset=${offset}&search=${search}&${positionParam}`
            );
            dispatch(
                setPlayersDraftRoom({
                    ...res.data,
                    limit,
                    search,
                    position: setPosition,
                    currentPage: 1,
                    offset,
                })
            );
        } catch (error) {
            return rejectWithValue(error?.message || "Fetch error");
        }
    }
);

export const searchPlayersDraftRoom = createAsyncThunk(
    "playersDraftRoom/searchPlayersDraftRoom",
    async (searchValue, { dispatch, getState, rejectWithValue }) => {
        try {
            const {
                playersDraftRoom: { position, playerIds, limit },
                stimulyatorDraftRoom: { chosenPlayerIds },
            } = getState();
            const offset = 0;
            const excludeIdsParam = buildExcludeParam(playerIds, chosenPlayerIds);
            const positionParam = buildPositionParam(position);
            const res = await axios.get(
                `${API_ENDPOINT}players/?${excludeIdsParam}&limit=${limit}&offset=${offset}&search=${searchValue}&${positionParam}`
            );

            dispatch(
                setPlayersDraftRoom({
                    ...res.data,
                    limit,
                    position,
                    search: searchValue,
                    currentPage: 1,
                    offset,
                })
            );
            dispatch(setSearchValue(searchValue));
        } catch (error) {
            return rejectWithValue(error?.message || "Fetch error");
        }
    }
);


export const pageNavDraftRoom = createAsyncThunk(
    "playersDraftRoom/pageNavDraftRoom",
    async (page, { dispatch, getState, rejectWithValue }) => {
        try {
            const {
                playersDraftRoom: { limit, search, position, playerIds },
                stimulyatorDraftRoom: { chosenPlayerIds },
            } = getState();
            const offset = (page - 1) * limit;
            const excludeIdsParam = buildExcludeParam(playerIds, chosenPlayerIds);
            const positionParam = buildPositionParam(position);
            const res = await axios.get(
                `${API_ENDPOINT}players/?${excludeIdsParam}&limit=${limit}&offset=${offset}&search=${search}&${positionParam}`
            );
            dispatch(
                setPlayersDraftRoom({
                    ...res.data,
                    limit,
                    search,
                    position,
                    currentPage: page,
                    offset,
                })
            );
        } catch (error) {
            return rejectWithValue(error?.message || "Fetch error");
        }
    }
);

export const playersDraftRoomSlice = createSlice({
    name: "playersDraftRoom",
    initialState,
    reducers: {
        setPlayersDraftRoom: (state, action) => {
            Object.assign(state, action.payload);
        },
        setSelectedPositions: (state, action) => {
            state.selectedPositions = action.payload;
        },
        setPositionDraftRoom: (state, action) => {
            state.position = action.payload;
        },
        setSearchValue: (state, action) => {
            state.search = action.payload;
        },
    },
    extraReducers: (builder) => {
        [
            getPlayersDraftRoom,
            positionPlayersDraftRoom,
            searchPlayersDraftRoom,
            pageNavDraftRoom,
        ].forEach((thunk) => {
            builder.addCase(thunk.pending, (state) => {
                state.loading = true;
            });
            builder.addCase(thunk.fulfilled, (state) => {
                state.loading = false;
            });
            builder.addCase(thunk.rejected, (state) => {
                state.loading = false;
            });
        });
    },
});

export const selectPlayersDraftRoom = (state) => state.playersDraftRoom;

export const {
    setPlayersDraftRoom,
    setPositionDraftRoom,
    setSelectedPositions,
    setSearchValue,
} = playersDraftRoomSlice.actions;

export default playersDraftRoomSlice.reducer;


export const positionActionDraftRoom = (selectedPositions) => (dispatch) => {
    const IOL_GROUP = ["iOL", "iOL_C"];
    dispatch(setSelectedPositions(selectedPositions));
    let apiPositions;
    if (selectedPositions.includes("All Positions")) {
        apiPositions = [];
    } else {
        apiPositions = selectedPositions.flatMap((pos) => {
            if (pos === "iOL") return IOL_GROUP;  // <-- expand to 2 API filters
            return pos;
        });
    }
    dispatch(setPositionDraftRoom(apiPositions));
    dispatch(positionPlayersDraftRoom(apiPositions));
};
